import React, {
    useState,
    useContext,
} from "react";

import {
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    InputAdornment,
} from "@mui/material";

import {
    ArrowBack,
    VerifiedUser,
    Email,
    Shield,
} from "@mui/icons-material";

import { motion } from "framer-motion";
import {
    useLocation,
    useNavigate,
} from "react-router-dom";

import api from "../api/api";
import { AuthContext } from "../AuthContext";


const COLORS = {
    navy: "#031B35",
    deepNavy: "#021426",
    navyBlue: "#062A4B",
    blue: "#063A6B",

    greenDark: "#075B2A",
    green: "#16803A",
    greenBright: "#20A84A",

    gold: "#F6C944",
    goldLight: "#FFE58A",
    yellow: "#EFA81C",

    white: "#FFFFFF",
    background: "#F7F9F8",

    text: "#172019",
    muted: "#66736B",

    border: "#DCE5DF",

    paleGreen: "#EFF9F1",
    paleGold: "#FFF9E5",

    error: "#B42318",
};


const LoginAuthentication = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const { setUser } = useContext(AuthContext);

    const email =
        location.state?.email ||
        localStorage.getItem("auth_email");

    const [code, setCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");


    const redirectUser = (user) => {
        const role = String(
            user?.user_type ||
            user?.role ||
            ""
        )
            .toLowerCase()
            .trim();

        switch (role) {
            case "owner":
                navigate("/owner-dashboard");
                break;

            case "admin":
                navigate("/newAdminDashboard");
                break;

            case "proprietor":
                navigate("/proprietorDashboard");
                break;

            case "principal":
                navigate("/principalDashboard");
                break;

            case "teacher":
                navigate("/teacher-dashboard");
                break;

            case "student":
                navigate("/student-dashboard");
                break;

            case "parent":
                navigate("/parentsDashboard");
                break;

            case "registry":
                navigate("/registerDashboard");
                break;

            case "vpi":
                navigate("/vpiDashboard");
                break;

            case "vpa":
                navigate("/newVpaDashboard");
                break;

            case "it personel":
            case "it personnel":
                navigate("/tTPersonalDashboard");
                break;

            case "business_manager":
            case "business manager":
                navigate("/businessManagerDashboard");
                break;

            case "sales_agent":
                navigate("/sales-dashboard");
                break;

            default:
                navigate("/");
        }
    };


    const verifyCode = async (e) => {
        e.preventDefault();

        const cleanCode = String(code || "").trim();

        if (!email) {
            setMessage(
                "Authentication email is missing. Please login again."
            );
            return;
        }

        if (!cleanCode) {
            setMessage(
                "Please enter the authentication code sent to your email."
            );
            return;
        }

        setLoading(true);
        setMessage("");

        try {
            const response = await api.post(
                "/api/users/verify-authentication-code/",
                {
                    email,
                    code: cleanCode,
                }
            );

            const access =
                response.data?.access;

            const refresh =
                response.data?.refresh;

            if (!access || !refresh) {
                throw new Error(
                    "Authentication failed. Tokens missing."
                );
            }

            localStorage.setItem(
                "access",
                access
            );

            localStorage.setItem(
                "refresh",
                refresh
            );

            window.dispatchEvent(
                new Event("user-logged-in")
            );

            const profile =
                await api.get(
                    "/api/user-profile/"
                );

            const user =
                profile.data;

            setUser(user);

            localStorage.setItem(
                "user",
                JSON.stringify(user)
            );

            localStorage.removeItem(
                "auth_email"
            );

            redirectUser(user);

        } catch (error) {
            console.error(
                "AUTHENTICATION ERROR:",
                error
            );

            setMessage(
                error?.response?.data?.error ||
                error?.response?.data?.detail ||
                "Invalid authentication code."
            );

        } finally {
            setLoading(false);
        }
    };


    return (
        <Box
            sx={{
                minHeight: "100dvh",
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: COLORS.background,
                px: {
                    xs: 2,
                    sm: 3,
                    md: 4,
                },
                py: {
                    xs: 4,
                    md: 6,
                },
            }}
        >

            <motion.div
                initial={{
                    opacity: 0,
                    scale: 0.94,
                }}
                animate={{
                    opacity: 1,
                    scale: 1,
                }}
                transition={{
                    duration: 0.65,
                    ease: [0.22, 1, 0.36, 1],
                }}
                style={{
                    width: "100%",
                    maxWidth: 470,
                }}
            >

                <Box
                    sx={{
                        width: "100%",
                        background:
                            COLORS.white,
                        border:
                            `1px solid ${COLORS.border}`,
                        borderRadius: {
                            xs: "18px",
                            sm: "22px",
                        },
                        overflow: "hidden",
                    }}
                >

                    {/* BRAND HEADER */}

                    <Box
                        sx={{
                            background:
                                COLORS.navy,
                            px: {
                                xs: 2.5,
                                sm: 4,
                            },
                            py: {
                                xs: 2.5,
                                sm: 3,
                            },
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                        }}
                    >

                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                flexShrink: 0,
                                borderRadius: "12px",
                                background:
                                    COLORS.white,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Shield
                                sx={{
                                    fontSize: 27,
                                    color:
                                        COLORS.green,
                                }}
                            />
                        </Box>

                        <Box>
                            <Typography
                                sx={{
                                    fontSize: {
                                        xs: ".72rem",
                                        sm: ".78rem",
                                    },
                                    fontWeight: 950,
                                    letterSpacing:
                                        ".8px",
                                    color:
                                        COLORS.white,
                                    lineHeight: 1.25,
                                }}
                            >
                                PASSION AIDS THE FUTURE
                            </Typography>

                            <Typography
                                sx={{
                                    mt: .35,
                                    fontSize: ".62rem",
                                    fontWeight: 700,
                                    color:
                                        "rgba(255,255,255,.65)",
                                    letterSpacing:
                                        ".4px",
                                }}
                            >
                                COLLEGE OF PROFESSIONAL
                                AND VOCATIONAL STUDIES
                            </Typography>
                        </Box>
                    </Box>


                    {/* GOLD BRAND LINE */}

                    <Box
                        sx={{
                            height: 4,
                            background:
                                COLORS.gold,
                        }}
                    />


                    {/* CONTENT */}

                    <Box
                        sx={{
                            px: {
                                xs: 2.5,
                                sm: 4,
                            },
                            py: {
                                xs: 3,
                                sm: 4,
                            },
                        }}
                    >

                        {/* VERIFICATION ICON */}

                        <Box
                            sx={{
                                display: "flex",
                                justifyContent:
                                    "center",
                                mb: 2.5,
                            }}
                        >
                            <motion.div
                                initial={{
                                    scale: 0.5,
                                    opacity: 0,
                                }}
                                animate={{
                                    scale: [0.5, 1.08, 1],
                                    opacity: 1,
                                }}
                                transition={{
                                    duration: 0.7,
                                    delay: 0.15,
                                    ease: "easeOut",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 70,
                                        height: 70,
                                        borderRadius:
                                            "50%",
                                        display: "flex",
                                        alignItems:
                                            "center",
                                        justifyContent:
                                            "center",
                                        background:
                                            COLORS.paleGreen,
                                        border:
                                            `1px solid ${COLORS.border}`,
                                    }}
                                >
                                    <VerifiedUser
                                        sx={{
                                            fontSize: 34,
                                            color:
                                                COLORS.green,
                                        }}
                                    />
                                </Box>
                            </motion.div>
                        </Box>


                        {/* TITLE */}

                        <Typography
                            sx={{
                                textAlign: "center",
                                fontSize: {
                                    xs: "1.75rem",
                                    sm: "2rem",
                                },
                                fontWeight: 950,
                                color:
                                    COLORS.navy,
                                letterSpacing:
                                    "-.7px",
                                lineHeight: 1.15,
                            }}
                        >
                            Verify Your Login
                        </Typography>


                        <Typography
                            sx={{
                                textAlign: "center",
                                mt: 1,
                                mb: 3.5,
                                color:
                                    COLORS.muted,
                                fontSize: ".88rem",
                                lineHeight: 1.65,
                                maxWidth: 370,
                                mx: "auto",
                            }}
                        >
                            Enter the authentication
                            code sent to your email
                            address to securely access
                            your account.
                        </Typography>


                        {/* EMAIL DISPLAY */}

                        {email && (
                            <Box
                                sx={{
                                    mb: 2.5,
                                    px: 1.6,
                                    py: 1.3,
                                    borderRadius:
                                        "10px",
                                    background:
                                        COLORS.paleGreen,
                                    border:
                                        `1px solid ${COLORS.border}`,
                                    display: "flex",
                                    alignItems:
                                        "center",
                                    gap: 1,
                                }}
                            >
                                <Email
                                    sx={{
                                        fontSize: 18,
                                        color:
                                            COLORS.green,
                                    }}
                                />

                                <Box
                                    sx={{
                                        minWidth: 0,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize:
                                                ".58rem",
                                            fontWeight:
                                                900,
                                            color:
                                                COLORS.green,
                                            letterSpacing:
                                                ".7px",
                                        }}
                                    >
                                        AUTHENTICATION EMAIL
                                    </Typography>

                                    <Typography
                                        sx={{
                                            mt: .25,
                                            fontSize:
                                                ".78rem",
                                            fontWeight:
                                                700,
                                            color:
                                                COLORS.text,
                                            overflow:
                                                "hidden",
                                            textOverflow:
                                                "ellipsis",
                                            whiteSpace:
                                                "nowrap",
                                        }}
                                    >
                                        {email}
                                    </Typography>
                                </Box>
                            </Box>
                        )}


                        {/* FORM */}

                        <form
                            onSubmit={verifyCode}
                        >

                            <Typography
                                sx={{
                                    mb: .8,
                                    fontSize:
                                        ".68rem",
                                    fontWeight:
                                        900,
                                    color:
                                        COLORS.text,
                                    letterSpacing:
                                        ".4px",
                                }}
                            >
                                AUTHENTICATION CODE
                            </Typography>


                            <TextField
                                fullWidth
                                autoFocus
                                value={code}
                                onChange={(e) =>
                                    setCode(
                                        e.target.value
                                    )
                                }
                                placeholder="Enter your code"
                                required
                                autoComplete="one-time-code"
                                inputProps={{
                                    inputMode:
                                        "numeric",
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <VerifiedUser
                                                sx={{
                                                    fontSize: 20,
                                                    color:
                                                        COLORS.green,
                                                }}
                                            />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root":
                                        {
                                            minHeight:
                                                58,
                                            borderRadius:
                                                "10px",
                                            background:
                                                COLORS.white,

                                            "& fieldset":
                                                {
                                                    borderColor:
                                                        COLORS.border,
                                                },

                                            "&:hover fieldset":
                                                {
                                                    borderColor:
                                                        COLORS.green,
                                                },

                                            "&.Mui-focused fieldset":
                                                {
                                                    borderColor:
                                                        COLORS.green,
                                                    borderWidth:
                                                        1.5,
                                                },
                                        },

                                    "& input":
                                        {
                                            fontSize:
                                                ".95rem",
                                            fontWeight:
                                                700,
                                            letterSpacing:
                                                ".8px",
                                        },
                                }}
                            />


                            {/* ERROR */}

                            {message && (
                                <motion.div
                                    initial={{
                                        opacity: 0,
                                        height: 0,
                                    }}
                                    animate={{
                                        opacity: 1,
                                        height: "auto",
                                    }}
                                    transition={{
                                        duration: 0.25,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            mt: 1.5,
                                            px: 1.5,
                                            py: 1.2,
                                            borderRadius:
                                                "9px",
                                            background:
                                                "#FFF4F2",
                                            border:
                                                "1px solid #F3C7C2",
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize:
                                                    ".76rem",
                                                fontWeight:
                                                    700,
                                                color:
                                                    COLORS.error,
                                                lineHeight:
                                                    1.5,
                                            }}
                                        >
                                            {message}
                                        </Typography>
                                    </Box>
                                </motion.div>
                            )}


                            {/* VERIFY */}

                            <Button
                                type="submit"
                                fullWidth
                                disabled={loading}
                                sx={{
                                    mt: 2.5,
                                    minHeight: 56,
                                    borderRadius:
                                        "10px",
                                    textTransform:
                                        "none",
                                    fontSize: ".9rem",
                                    fontWeight: 900,
                                    color:
                                        COLORS.white,

                                    background:
                                        COLORS.green,

                                    boxShadow:
                                        "none",

                                    "&:hover":
                                        {
                                            background:
                                                COLORS.greenDark,
                                            boxShadow:
                                                "none",
                                        },

                                    "&:disabled":
                                        {
                                            background:
                                                "#AAB8AF",
                                            color:
                                                COLORS.white,
                                        },

                                    transition:
                                        "background .2s ease",
                                }}
                            >
                                {loading ? (
                                    <Box
                                        sx={{
                                            display:
                                                "flex",
                                            alignItems:
                                                "center",
                                            justifyContent:
                                                "center",
                                            gap: 1,
                                        }}
                                    >
                                        <CircularProgress
                                            size={20}
                                            thickness={5}
                                            sx={{
                                                color:
                                                    COLORS.white,
                                            }}
                                        />

                                        Verifying...
                                    </Box>
                                ) : (
                                    <Box
                                        sx={{
                                            display:
                                                "flex",
                                            alignItems:
                                                "center",
                                            justifyContent:
                                                "center",
                                            gap: .8,
                                        }}
                                    >
                                        Verify & Continue
                                    </Box>
                                )}
                            </Button>


                            {/* BACK */}

                            <Button
                                fullWidth
                                variant="text"
                                onClick={() =>
                                    navigate(
                                        "/login"
                                    )
                                }
                                startIcon={
                                    <ArrowBack
                                        sx={{
                                            fontSize: 18,
                                        }}
                                    />
                                }
                                sx={{
                                    mt: 1.2,
                                    minHeight: 46,
                                    borderRadius:
                                        "9px",
                                    textTransform:
                                        "none",
                                    color:
                                        COLORS.muted,
                                    fontSize: ".78rem",
                                    fontWeight: 800,

                                    "&:hover":
                                        {
                                            background:
                                                COLORS.paleGreen,
                                            color:
                                                COLORS.navy,
                                        },
                                }}
                            >
                                Back To Login
                            </Button>

                        </form>


                        {/* SECURITY NOTE */}

                        <Box
                            sx={{
                                mt: 3,
                                pt: 2.5,
                                borderTop:
                                    `1px solid ${COLORS.border}`,
                                textAlign:
                                    "center",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize:
                                        ".62rem",
                                    color:
                                        COLORS.muted,
                                    lineHeight:
                                        1.6,
                                }}
                            >
                                Your verification code
                                helps protect your
                                account and personal
                                information.
                            </Typography>
                        </Box>

                    </Box>


                    {/* FOOTER */}

                    <Box
                        sx={{
                            px: {
                                xs: 2.5,
                                sm: 4,
                            },
                            py: 1.8,
                            background:
                                "#FAFCFA",
                            borderTop:
                                `1px solid ${COLORS.border}`,
                            textAlign:
                                "center",
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize:
                                    ".6rem",
                                fontWeight:
                                    800,
                                letterSpacing:
                                    ".5px",
                                color:
                                    COLORS.green,
                            }}
                        >
                            PASSION • EDUCATION • SKILLS • LEADERSHIP • OPPORTUNITY
                        </Typography>
                    </Box>

                </Box>

            </motion.div>

        </Box>
    );
};

export default LoginAuthentication;